// /src/pages/CaptureDevicesPage.js
// Aegis Capture — device enrollment.
//
// An enrollment code binds ONE handset to THIS Aegis server. Scanning it is not
// a login: the person still signs in with their own credentials afterwards.
// That split is why this page exists separately from Users — a shared handset on
// a bench is enrolled once and different people sign in on it through the shift.
//
// Behind the `aegis-capture` module and administrator rights: holding the module
// lets you use a handset, authorising new ones is a different thing.
import React, { useEffect, useRef, useState } from "react";

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(async (res) => {
    if (!res.ok) {
      const raw = await res.text();
      let msg = raw;
      try { msg = JSON.parse(raw).error || raw; } catch { /* keep raw text */ }
      throw new Error(msg || `${res.status} ${res.statusText}`);
    }
    return res.status === 204 ? null : res.json();
  });
}

// Fetch an authenticated image and hand back an object URL.
//
// A plain <img src="/api/..."> cannot be used here: the browser sends cookies
// but NOT the Authorization header, so the request arrives unauthenticated and
// renders as a broken image. Putting the token in the query string would work
// and is a worse idea -- URLs end up in history, proxy logs and Referer headers,
// and this one addresses a live credential.
async function fetchAuthedImage(path) {
  const token = localStorage.getItem("token");
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { msg = (await res.json()).error || msg; } catch { /* not json */ }
    throw new Error(msg);
  }
  return URL.createObjectURL(await res.blob());
}

const STATE_STYLE = {
  enrolled: { color: "#1a7f37", label: "Enrolled" },
  pending:  { color: "#9a6700", label: "Waiting to be scanned" },
  expired:  { color: "#6e7781", label: "Expired" },
  revoked:  { color: "#cf222e", label: "Revoked" },
};

function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export default function CaptureDevicesPage() {
  const [rows, setRows] = useState([]);
  const [deployment, setDeployment] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // The code currently being shown as a QR. Held in state only while the admin
  // has it on screen; it is never persisted anywhere in the browser.
  const [showing, setShowing] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [qrErr, setQrErr] = useState("");
  // Revoked and expired enrollments are hidden by default -- the working list
  // is what is live. They are never deleted server-side (the record of which
  // handset filed which document has to outlive the handset), so this is a view
  // filter, not a purge, and the toggle keeps them reachable.
  const [showInactive, setShowInactive] = useState(false);
  const qrUrlRef = useRef(null);

  // Load the QR whenever a different code is being shown, and release the
  // previous object URL -- these hold the image bytes in memory until revoked.
  useEffect(() => {
    let cancelled = false;

    function release() {
      if (qrUrlRef.current) {
        URL.revokeObjectURL(qrUrlRef.current);
        qrUrlRef.current = null;
      }
    }

    release();
    setQrUrl(null);
    setQrErr("");
    if (!showing?.id) return undefined;

    fetchAuthedImage(`/api/mobile/admin/enrollments/${showing.id}/qr.png`)
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        qrUrlRef.current = url;
        setQrUrl(url);
      })
      .catch((e) => { if (!cancelled) setQrErr(e.message); });

    return () => { cancelled = true; release(); };
  }, [showing?.id]);

  async function load() {
    try {
      setLoading(true);
      setErr("");
      const data = await api("/mobile/admin/enrollments");
      setRows(data.enrollments || []);
      setDeployment(data.deployment || null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // A pending code expires while the page is open, so keep the list honest
  // rather than leaving a QR on screen that has quietly stopped working.
  useEffect(() => {
    if (!rows.some((r) => r.state === "pending")) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [rows]);

  async function issue(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      setErr("");
      const res = await api("/mobile/admin/enrollments", {
        method: "POST",
        body: { label: (f.get("label") || "").trim() || null },
      });
      e.target.reset();
      setShowing({ id: res.id, expires_at: res.expires_at });
      load();
    } catch (e2) {
      setErr(e2.message);
    }
  }

  async function revoke(row) {
    const what = row.state === "pending"
      ? "This code will stop working before anyone scans it."
      : `${row.label || row.device_id} will stop working on its next request. `
        + "Anything it has already uploaded is kept.";
    if (!window.confirm(`Revoke this enrollment?\n\n${what}`)) return;
    try {
      setErr("");
      await api(`/mobile/admin/enrollments/${row.id}`, { method: "DELETE" });
      if (showing?.id === row.id) setShowing(null);
      load();
    } catch (e) {
      setErr(e.message);
    }
  }

  const LIVE = ["enrolled", "pending"];
  const visible = showInactive ? rows : rows.filter((r) => LIVE.includes(r.state));
  const hiddenCount = rows.length - rows.filter((r) => LIVE.includes(r.state)).length;

  return (
    <div style={{ padding: 16 }}>
      <h2>Aegis Capture — devices</h2>
      <p style={{ opacity: 0.75, marginTop: -6, maxWidth: 720 }}>
        An enrollment code connects one handset to this Aegis server. It is not a
        login — after scanning, the user signs in with their own Aegis email and
        password. Codes are single use and expire shortly after they are created.
      </p>
      {deployment && (
        <p style={{ opacity: 0.6, fontSize: 13 }}>
          Handsets will be bound to <strong>{deployment.label}</strong> ({deployment.api_url})
        </p>
      )}

      {err && (
        <div style={{ background: "#ffe9e9", border: "1px solid #f5c2c7",
                      padding: 10, borderRadius: 6, margin: "12px 0" }}>
          {err}
        </div>
      )}

      <form onSubmit={issue} style={{ display: "flex", gap: 8, alignItems: "center", margin: "16px 0" }}>
        <input name="label" placeholder="Which handset? e.g. Jim iPhone"
               style={{ padding: 8, minWidth: 260 }} />
        <button type="submit">Generate enrollment code</button>
      </form>

      {showing && (
        <div style={{ border: "1px solid #d0d7de", borderRadius: 8, padding: 16,
                      margin: "16px 0", maxWidth: 420 }}>
          <strong>Scan this with Aegis Capture on the handset</strong>
          <div style={{ margin: "12px 0", minHeight: 260 }}>
            {/* Rendered by the server and fetched with the auth header, so the
                payload never becomes a URL the browser records anywhere. */}
            {qrErr ? (
              <div style={{ color: "#cf222e" }}>Could not load the QR code: {qrErr}</div>
            ) : qrUrl ? (
              <img
                src={qrUrl}
                alt="Enrollment QR code"
                style={{ width: 260, height: 260, imageRendering: "pixelated" }}
              />
            ) : (
              <div style={{ opacity: 0.6 }}>Generating…</div>
            )}
          </div>
          <div style={{ fontSize: 13, opacity: 0.75 }}>
            Expires {when(showing.expires_at)}. Single use.
          </div>
          <button onClick={() => setShowing(null)} style={{ marginTop: 10 }}>Done</button>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginTop: 24 }}>
        <h3 style={{ margin: 0 }}>Enrolled devices</h3>
        {hiddenCount > 0 && (
          <label style={{ fontSize: 13, opacity: 0.75, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            Show {hiddenCount} revoked / expired
          </label>
        )}
      </div>
      {loading && <p style={{ opacity: 0.6 }}>Loading…</p>}
      {!loading && visible.length === 0 && (
        <p style={{ opacity: 0.6 }}>
          {rows.length === 0
            ? "No handsets enrolled yet."
            : "No active handsets. Generate an enrollment code above."}
        </p>
      )}

      {visible.length > 0 && (
        <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 1000 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #d0d7de" }}>
              <th style={{ padding: "6px 8px" }}>Handset</th>
              <th style={{ padding: "6px 8px" }}>State</th>
              <th style={{ padding: "6px 8px" }}>Platform</th>
              <th style={{ padding: "6px 8px" }}>Enrolled</th>
              <th style={{ padding: "6px 8px" }}>Last seen</th>
              <th style={{ padding: "6px 8px" }}>Issued by</th>
              <th style={{ padding: "6px 8px" }} />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const s = STATE_STYLE[r.state] || { color: "#6e7781", label: r.state };
              return (
                <tr key={r.id} style={{ borderBottom: "1px solid #eaeef2" }}>
                  <td style={{ padding: "6px 8px" }}>{r.label || <em style={{ opacity: 0.6 }}>unnamed</em>}</td>
                  <td style={{ padding: "6px 8px", color: s.color, fontWeight: 600 }}>{s.label}</td>
                  <td style={{ padding: "6px 8px" }}>{r.platform || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{when(r.redeemed_at)}</td>
                  <td style={{ padding: "6px 8px" }}>{when(r.last_seen_at)}</td>
                  <td style={{ padding: "6px 8px", fontSize: 13, opacity: 0.8 }}>{r.created_by_email || "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {r.state === "pending" && (
                      <button onClick={() => setShowing({ id: r.id, expires_at: r.code_expires_at })}
                              style={{ marginRight: 8 }}>
                        Show QR
                      </button>
                    )}
                    {r.state !== "revoked" && (
                      <button onClick={() => revoke(r)}>Revoke</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
